import {
  ApolloClient,
  ApolloLink,
  CombinedGraphQLErrors,
  type DefaultContext,
  type FetchPolicy,
  gql,
  InMemoryCache,
  type InMemoryCacheConfig,
  ServerError,
  type TypedDocumentNode,
} from '@apollo/client';
import {
  ApiError,
  type Config,
  environment,
  type ErrorHandler,
  GqlClientError,
  isNotUndefinedOrNull,
  type Logger,
  truncateString,
  TYPES,
  UnexpectedBackendError,
  zenObservableToRxJS,
} from '@bizzdesign/sdk-internal';
import { generateUUID } from '@bizzdesign/sdk-internal/uuid';
import { fetch } from 'cross-fetch';
import { inject, injectable } from 'inversify';
import { type AuthTokenProvider } from '@bizzdesign/sdk-auth';

import { type GraphqlAPIClient } from './interfaces';
import { Genql } from '@bizzdesign/bizzdesign-sdk-type-genql';
import { RetryLink } from '@apollo/client/link/retry';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { customFetchMergedQueries } from './merge-queries';
import { getMainDefinition } from '@apollo/client/utilities';
import { InvariantError } from '@apollo/client/utilities/invariant';
import { Kind, OperationTypeNode } from 'graphql/language';
import { print, visit } from 'graphql';
import { type Observable, tap } from 'rxjs';
import { disableFragmentWarnings } from 'graphql-tag';
import { AppSyncWebSocketLink } from './appsync-ws-link';
import { type MaybeMasked } from '@apollo/client/masking';
import { SetContextLink } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import { HttpLink } from '@apollo/client/link/http';
import QueryResult = ApolloClient.QueryResult;
import MutateResult = ApolloClient.MutateResult;

export const MAX_LINK_RETRY_ATTEMPTS = 3;

/**
 * Apollo invariant message ids raised when the client itself aborts an operation
 * that is still in flight, rather than the operation failing on its own:
 * `89` (`QueryManager stopped while query was in flight`) and `91`
 * (`Store reset while query was in flight (not completed in link chain)`).
 *
 * @see {@link https://go.apollo.dev/c/err | Apollo error message reference}
 */
const APOLLO_CLIENT_ABORT_INVARIANT_IDS: ReadonlySet<number> = new Set([
  89, 91,
]);

/**
 * Development-build texts for {@link APOLLO_CLIENT_ABORT_INVARIANT_IDS}. Apollo only
 * minifies invariant messages in production builds, so both forms must be matched.
 */
const APOLLO_CLIENT_ABORT_MESSAGES: readonly string[] = [
  'QueryManager stopped while query was in flight',
  'Store reset while query was in flight',
];

const APOLLO_INVARIANT_LINK_MARKER = 'go.apollo.dev/c/err#';

/**
 * Extracts the numeric Apollo invariant message id from a minified `InvariantError`.
 *
 * @param message - The error message to inspect. Production builds replace the text
 * with a `go.apollo.dev/c/err#<url-encoded-json>` link whose payload carries the id.
 * @returns The invariant message id, or `undefined` when the message is not a minified
 * Apollo invariant link.
 */
function extractApolloInvariantId(message: string): number | undefined {
  const markerIndex = message.indexOf(APOLLO_INVARIANT_LINK_MARKER);
  if (markerIndex === -1) {
    return undefined;
  }
  try {
    const fragment = message.slice(
      markerIndex + APOLLO_INVARIANT_LINK_MARKER.length,
    );
    const decoded: unknown = JSON.parse(decodeURIComponent(fragment));
    if (
      typeof decoded === 'object' &&
      decoded !== null &&
      'message' in decoded &&
      typeof decoded.message === 'number'
    ) {
      return decoded.message;
    }
    return undefined;
  } catch {
    return undefined;
  }
}

/**
 * Determines whether an error is Apollo aborting an in-flight operation from the client
 * side — a store wipe or a client teardown — rather than a genuine transport or server
 * failure.
 *
 * @param error - The rejection thrown by an in-flight Apollo operation.
 * @returns `true` when the error is an Apollo `InvariantError` for one of
 * {@link APOLLO_CLIENT_ABORT_INVARIANT_IDS}, matched via either the development-build
 * message text or the minified production id.
 *
 * @remarks Such an abort means the caller's request never reached a verdict; it is not a
 * backend fault and is logged at `warn` rather than `error` so it does not read as an
 * unexpected failure in Sentry.
 */
export function isApolloClientAbortError(error: unknown): boolean {
  // Gate on the Apollo type first: a server error echoing the same text is a real failure.
  if (!(error instanceof InvariantError)) {
    return false;
  }
  if (
    APOLLO_CLIENT_ABORT_MESSAGES.some((text) => error.message.includes(text))
  ) {
    return true;
  }
  const invariantId = extractApolloInvariantId(error.message);
  return (
    invariantId !== undefined &&
    APOLLO_CLIENT_ABORT_INVARIANT_IDS.has(invariantId)
  );
}

@injectable()
export class ApolloGraphqlAPIClient implements GraphqlAPIClient {
  private readonly apolloClient: ApolloClient;
  private readonly wsLink: AppSyncWebSocketLink;

  constructor(
    @inject(TYPES.Logger) private readonly logger: Logger,
    @inject(TYPES.Config) private readonly config: Config,
    @inject(TYPES.AuthTokenProvider)
    private readonly authTokenProvider: AuthTokenProvider,
    @inject(TYPES.ErrorHandler)
    private readonly errorHandler: ErrorHandler,
  ) {
    disableFragmentWarnings();
    this.wsLink = new AppSyncWebSocketLink(
      this.authTokenProvider,
      this.logger,
      this.config,
    );
    this.apolloClient = this.createAuthConfiguredApolloLinkBasedClient();
  }

  /**
   * Executes a strongly typed GraphQL query built with Genql and returns the `data` payload.
   *
   * @typeParam Q - Genql selection type that describes the shape of the query and result.
   *
   * @param options - Query execution options.
   * @param options.query - {@link Genql.QueryGenqlSelection} selection tree (fields and variables)
   * that defines the query.
   * @param options.useStubbing - When `true` adds `@client` Apollo Client directive. @see {@link https://www.apollographql.com/docs/react/local-state/managing-state-with-field-policies#querying | Apollo Client: Local-only fields (@client)}
   * @param options.cacheable - When `true` lets Apollo satisfy the query from its `InMemoryCache`
   *   when possible, falling back to the network if not present. When `cacheable: false`, results
   *   are always fetched from the network.
   *
   * @returns A promise that resolves to the typed GraphQL result (`data`) for the given selection.
   */
  async query<Q extends Genql.QueryGenqlSelection>(options: {
    query: Q;
    useStubbing?: boolean;
    cacheable?: boolean;
  }): Promise<MaybeMasked<Genql.QueryResult<Q>>> {
    this.logger.debug({
      context: 'GraphqlAPIClient',
      msg: 'GraphQL query',
      payload: options.query,
    });
    const { query, variables } = Genql.generateQueryOp(options.query);
    const fetchPolicy: FetchPolicy =
      (options.cacheable ?? false) &&
      (this.config.enableCache?.attributeCategoryDefinitions ?? false)
        ? 'cache-first'
        : 'no-cache';
    const doc =
      options.useStubbing === true &&
      !environment.APOLLO_CLIENT_DISABLE_STUBBING_BEHAVIOUR
        ? this.documentNodeWithClientAnnotations(gql(query))
        : gql(query);
    const result = await this.apolloClient
      .query<Genql.QueryResult<Q>>({
        query: doc,
        variables: variables,
        fetchPolicy,
      })
      .catch(
        this.apolloErrorHandler<
          Promise<QueryResult<MaybeMasked<Genql.QueryResult<Q>>>>
        >,
      );

    this.logger.debug({
      context: 'GraphqlAPIClient',
      msg: 'GraphQL query result',
      payload: result,
    });
    if (result.data === undefined) {
      throw new ApiError(
        'No data received from the GraphQL query, got error',
        result.error,
      );
    }
    return result.data;
  }

  subscription<S extends Genql.SubscriptionGenqlSelection>(options: {
    subscription: S;
    context?: DefaultContext;
  }): Observable<ApolloLink.Result<Genql.SubscriptionResult<S>>> {
    this.logger.debug({
      context: 'GraphqlAPIClient',
      msg: 'GraphQL subscription',
      payload: options.subscription,
    });
    const { query, variables } = Genql.generateSubscriptionOp(
      options.subscription,
    );

    return zenObservableToRxJS(
      // This subscribe call will return an observable.
      this.apolloClient.subscribe<Genql.SubscriptionResult<S>>({
        query: gql(query),
        variables: variables,
        context: options.context,
      }),
    ).pipe(
      tap((incoming) =>
        this.logger.debug({
          context: 'GraphqlAPIClient',
          msg: 'GraphQL Subscription output',
          payload: incoming,
        }),
      ),
    );
  }

  /**
   * Empties the Apollo `InMemoryCache`, so subsequent operations start from a clean slate.
   *
   * @returns A promise that resolves once the cached data has been discarded.
   *
   * @remarks Queries already in flight are left running and still resolve normally.
   * `resetStore()`/`clearStore()` are deliberately avoided here: both funnel through
   * `QueryManager.clearStore()`, which rejects every pending fetch with Apollo invariant
   * `91`. Their only added behaviour is refetching watched queries, and this client never
   * calls `watchQuery`, so resetting the cache directly is equivalent minus the aborts.
   */
  async cacheReset(): Promise<void> {
    await this.apolloClient.cache.reset();
    this.logger.debug({
      context: 'GraphqlAPIClient',
      msg: 'Apollo cache reset',
    });
  }

  /**
   * Disposes the Apollo client and releases any transport-level resources it owns,
   * making the instance safe to drop. Stops the WebSocket link timers, clears the
   * Apollo cache and stops the query manager.
   *
   * @returns A promise that resolves once the client has been stopped and its cache cleared.
   */
  async destroy(): Promise<void> {
    this.wsLink.dispose();
    try {
      await this.apolloClient.clearStore();
    } finally {
      this.apolloClient.stop();
      this.logger.debug({
        context: 'GraphqlAPIClient',
        msg: 'Apollo client destroyed',
      });
    }
  }

  async mutation<M extends Genql.MutationGenqlSelection>(options: {
    mutation: M;
    useStubbing?: boolean;
  }): Promise<Genql.MutationResult<M>> {
    this.logger.debug({
      context: 'GraphqlAPIClient',
      msg: 'GraphQL mutation',
      payload: options.mutation,
    });
    const { query, variables } = Genql.generateMutationOp(options.mutation);

    const result = await this.apolloClient
      .mutate<Genql.MutationResult<M>>({
        mutation:
          options.useStubbing === true &&
          !environment.APOLLO_CLIENT_DISABLE_STUBBING_BEHAVIOUR
            ? this.documentNodeWithClientAnnotations(gql(query))
            : gql(query),
        variables: variables,
        fetchPolicy: 'no-cache',
      })
      .catch(
        this.apolloErrorHandler<Promise<MutateResult<Genql.MutationResult<M>>>>,
      );
    this.logger.debug({
      context: 'GraphqlAPIClient',
      msg: 'GraphQL mutation result',
      payload: result,
    });
    if (result.data === undefined) {
      throw new ApiError(
        'No data received from the GraphQL mutation',
        result.error,
      );
    }
    return result.data;
  }

  createAuthConfiguredApolloLinkBasedClient(): ApolloClient {
    const authContext = new SetContextLink(
      async ({ headers }): Promise<DefaultContext> => {
        const token = await this.authTokenProvider.getToken();
        return {
          headers: {
            ...headers,
            Authorization: `Bearer ${token}`,
            'X-Bizz-Sdk-Request-Id':
              this.config.sdkRequestIdProvider?.() ?? generateUUID(),
          },
        };
      },
    );

    const errorLink = new ErrorLink(({ error, operation }) => {
      const sdkRequestId: string =
        operation.getContext()?.['headers']?.['X-Bizz-Sdk-Request-Id'] ?? '';
      const graphqlQuery = truncateString(print(operation.query), 200);
      // Raw console.error (not this.logger): Sentry's breadcrumbs integration patches
      // console, so this reaches Sentry with request context whatever the logger config.
      console.error(error?.message, {
        error: error,
        context: 'GraphqlAPIClient',
        graphqlQuery: graphqlQuery,
        sdkRequestId: sdkRequestId,
      });

      if (CombinedGraphQLErrors.is(error)) {
        error.errors.forEach((graphQLErr) => {
          const errorType =
            'errorType' in graphQLErr
              ? String(graphQLErr['errorType'])
              : 'UnknownError';
          if (
            errorType === 'Lambda:Unhandled' ||
            errorType === 'UnknownError'
          ) {
            this.errorHandler.notify(
              new UnexpectedBackendError(
                `[${errorType}] ${graphQLErr.message}`,
                graphQLErr,
              ),
            );
          }
        });
      }
    });
    const retryLink = new RetryLink({
      attempts: {
        max: MAX_LINK_RETRY_ATTEMPTS,
        retryIf: (error, operation): boolean => {
          const definition = getMainDefinition(operation.query);
          // Mutations aren't safe to retry: a lost/slow response can race a second
          // execution against the first still finishing server-side (e.g. two writes
          // racing an optimistic-lock check on the same record).
          if (
            definition.kind === Kind.OPERATION_DEFINITION &&
            definition.operation === OperationTypeNode.MUTATION
          ) {
            return false;
          }
          if (ServerError.is(error)) {
            return [500, 502, 503, 504].some(
              (allowedCode) => allowedCode === error.statusCode,
            );
          }
          return error !== null && error !== undefined;
        },
      },
      delay: { initial: 100, max: 2000, jitter: true }, // exponential with jitter
    });

    let httpLink: ApolloLink;
    const plainHttpLink = new HttpLink({
      uri: this.config.graphqlEndpoint,
      fetch: fetch,
    });

    if (this.config.queryBatching.enabled) {
      const queriesBatchHttpLink = new BatchHttpLink({
        uri: this.config.graphqlEndpoint,
        fetch: customFetchMergedQueries, // Automatically merge the batched queries for Appsync
        batchMax: this.config.queryBatching.batchMaxRequests ?? 10,
        batchInterval: this.config.queryBatching.batchInterval ?? 250,
        batchKey: (): string => 'unify', // Without a static key, no batching will occur.
      });
      // The split is used to apply query batching/merging just for Queries.
      // Handle Mutations and Subscriptions with a plain HttpLink.
      httpLink = ApolloLink.split(
        ({ query }) => {
          const definition = getMainDefinition(query);
          return (
            definition.kind === Kind.OPERATION_DEFINITION &&
            definition.operation === OperationTypeNode.QUERY
          );
        },
        queriesBatchHttpLink,
        plainHttpLink,
      );
    } else {
      httpLink = plainHttpLink;
    }

    // Split http / subscription as recommended by Apollo docs
    const splitSubscriptionAndHttpLink = ApolloLink.split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return (
          definition.kind === 'OperationDefinition' &&
          definition.operation === 'subscription'
        );
      },
      this.wsLink,
      httpLink,
    );

    return new ApolloClient({
      devtools: {
        enabled: this.config.disableApolloDevTools !== true, // required if you want to inspect Apollo's cache with 'Apollo Client Devtools'
      },
      link: ApolloLink.from([
        authContext,
        errorLink,
        retryLink,
        splitSubscriptionAndHttpLink,
      ]),
      ...(this.config.enableCache?.attributeCategoryDefinitions === true
        ? {
            connectToDevTools: this.config.disableApolloDevTools !== true,
            cache: new InMemoryCache(
              ApolloGraphqlAPIClient.apolloMemoryCacheConfig(),
            ),
          }
        : {
            cache: new InMemoryCache(),
          }),
    });
  }

  private readonly apolloErrorHandler = <T>(err: unknown): T => {
    if (isApolloClientAbortError(err)) {
      this.logger.warn({
        context: 'GraphqlAPIClient',
        msg: 'GraphQL operation aborted by the Apollo client (store wipe or teardown).',
        payload: err,
      });
    } else {
      this.logger.error({
        context: 'GraphqlAPIClient',
        msg: 'Error occurred during the graphql calls.',
        payload: err,
      });
    }
    if (isNotUndefinedOrNull(err) && err instanceof Error) {
      throw new GqlClientError(`GraphQL Client Error: ${err?.message}`, err);
    }
    throw err;
  };

  private documentNodeWithClientAnnotations<T, Y>(
    documentNode: TypedDocumentNode<T, Y>,
  ): TypedDocumentNode<T, Y> {
    const editedDocumentNode = visit(documentNode, {
      Name(node, key, parent) {
        let editedNode = node;
        if (
          parent &&
          !Array.isArray(parent) &&
          'kind' in parent &&
          parent.kind === Kind.FIELD
        ) {
          editedNode = {
            ...node,
            value: `${node.value} @client`,
          };
        }
        return editedNode;
      },
    });
    return gql(print(editedDocumentNode));
  }

  /**
   * Provides the default `InMemoryCache` configuration used by this client.
   *
   * Defines `typePolicies` that customize cache keys for `AttributeCategoryDefinition` that
   * uses a composite cache key
   *
   * @internal
   */
  private static apolloMemoryCacheConfig(): InMemoryCacheConfig {
    return {
      typePolicies: {
        AttributeCategoryDefinition: {
          keyFields: ['name', ['id']],
        },
        NameOutput: { keyFields: ['id'] },
      },
    };
  }
}
